import { Component, computed, inject, input, type OnInit, signal } from "@angular/core";
import { RouterLink } from "@angular/router";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { filter } from "rxjs";

import type { AdaaBooleanType } from "../../../../../../../../adaa-types";
import { TranslateTokenPipe } from "../../../../../../../core/pipes";
import { AdaaHelper } from "../../../../../../../core/utils";
import { Constants } from "../../../../../../constants/constants";
import { AdaaBoolean, TrackEntryClassification } from "../../../../../../constants/enums";
import { MetricsDetailModelType, PropTypeModelType, WorkflowDiffVersionType } from "../../../../../../models";
import { LanguageService, PropertiesService } from "../../../../../../services";
import { FormulaViewComponent, NoFormulaViewComponent, TargetsFormulaViewComponent } from "../../_lib";
import { FormulaMetricType, NoFormulaMetricType } from "../../_lib/data-entry-diff/types";

const ITEMS_ENTRY_CLASSIFICATIONS: number[] = [
  TrackEntryClassification.OPERATIONS,
  TrackEntryClassification.STRATEGIC_TASKS,
];

@Component({
  selector: "adaa-data-entry-screen",
  imports: [
    TranslatePipe,
    TranslateTokenPipe,
    RouterLink,
    NoFormulaViewComponent,
    FormulaViewComponent,
    TargetsFormulaViewComponent,
  ],
  templateUrl: "./data-entry-screen.html",
  styleUrl: "./data-entry-screen.scss",
})
export class DataEntryScreenComponent implements OnInit {
  readonly languageService = inject(LanguageService);
  private _propertiesService = inject(PropertiesService);
  private _translateService = inject(TranslateService);

  showOnlyNew = input.required<boolean>();
  newDataentryObject = input<WorkflowDiffVersionType>();
  oldDataentryObject = input<WorkflowDiffVersionType>();
  viewOnlyDifferences = input<boolean>(false);
  isForecastedValues = input<boolean>(false);
  isNewEntries = input<boolean>(false);
  isAuditTrail = input<boolean>(false);

  months = signal<PropTypeModelType[]>([]);
  trends = signal<PropTypeModelType[]>([]);
  ytpCalcs = signal<PropTypeModelType[]>([]);
  frequencies = signal<PropTypeModelType[]>([]);
  measurementUnits = signal<PropTypeModelType[]>([]);
  title = signal<string>("");

  isBounded = computed(() => this.newDataentryObject()?.trend === Constants.TREND.BOUNDED);
  isItemsEntry = computed(() => {
    const dataentry = this.newDataentryObject();
    if (dataentry?.kpiType !== Constants.KPI_TYPE.AIKPI) return false;
    if (!dataentry.entryClassification) return false;

    return ITEMS_ENTRY_CLASSIFICATIONS.includes(dataentry.entryClassification);
  });
  isBaselineKpi = computed(() => this.newDataentryObject()?.baselineKpi === AdaaBoolean.Y);
  isNoFormula = computed(() => {
    if (this.isNewEntries()) return true;
    const metricEntities = this.newDataentryObject()?.metricEntities;
    const dataEntryRequests = this.newDataentryObject()?.dataEntryRequests;
    const formula = this.newDataentryObject()?.formula;

    return !metricEntities && !dataEntryRequests && formula === Constants.FORMULA_STRING.NOFORMULA;
  });
  isFormula = computed(() => {
    const { dataEntryRequests, govKPIValues } = this._entryLists(this.newDataentryObject());

    return !this.isNoFormula() && (dataEntryRequests.length > 0 || govKPIValues.length > 0);
  });
  hasDetailedTargets = computed(() => {
    const hasDetailedTargets = this.newDataentryObject()?.hasTarget === AdaaBoolean.Y;
    return hasDetailedTargets;
  });
  metricDetails = computed(() => this.newDataentryObject()?.metricDetails as MetricsDetailModelType[]);
  newDataEntryRequests = computed(() => {
    const { dataEntryRequests, govKPIValues } = this._entryLists(this.newDataentryObject());

    return dataEntryRequests.length > 0 ? dataEntryRequests : govKPIValues;
  });
  oldDataEntryRequests = computed(() => {
    const { dataEntryRequests, govKPIValues } = this._entryLists(this.oldDataentryObject());

    return dataEntryRequests.length > 0 ? dataEntryRequests : govKPIValues;
  });

  private _noFormulaMetrics(object: WorkflowDiffVersionType | undefined) {
    return (object?.metrics ?? object?.dataEntries ?? []) as NoFormulaMetricType[];
  }

  private _entryLists(object: WorkflowDiffVersionType | undefined) {
    return {
      dataEntryRequests: (object?.dataEntryRequests ?? []) as FormulaMetricType[],
      govKPIValues: (object?.govKPIValues ?? []) as FormulaMetricType[],
    };
  }

  newNoFormulaMetrics = computed(() => this._noFormulaMetrics(this.newDataentryObject()));
  oldNoFormulaMetrics = computed(() => this._noFormulaMetrics(this.oldDataentryObject()));

  readonly isDefinedAndNotEmpty = AdaaHelper.isDefinedAndNotEmpty.bind(AdaaHelper);

  public ngOnInit() {
    this._fetchConstants();
  }

  public getKpitypeName(kpiType?: number) {
    if (kpiType == null) return undefined;
    return {
      [Constants.KPI_TYPE.NKPI]: "nkpi",
      [Constants.KPI_TYPE.SKPI]: "skpi",
      [Constants.KPI_TYPE.MOKPI]: "mokpi",
      [Constants.KPI_TYPE.IPTKPI]: "iptkpi",
      [Constants.KPI_TYPE.MTKPI]: "mtkpi",
      [Constants.KPI_TYPE.NTKPI]: "ntkpi",
      [Constants.KPI_TYPE.DTKPI]: "dtkpi",
      [Constants.KPI_TYPE.OPM]: "opm",
      [Constants.KPI_TYPE.UAE]: "uae",
      [Constants.KPI_TYPE.EKPI]: "ekpi",
      [Constants.KPI_TYPE.SRVKPI]: "srvkpi",
      [Constants.KPI_TYPE.IMKPI]: "imkpi",
      [Constants.KPI_TYPE.AIKPI]: "aikpi",
    }[kpiType];
  }

  public getActivityTypeName(activityType: unknown) {
    if (typeof activityType !== "number") return undefined;
    return {
      [Constants.ACTIVITY_TYPES_DS3.M1_MILESTONE]: "m1",
      [Constants.ACTIVITY_TYPES_DS3.OPERATIONAL_ACTIVITY]: "operational",
      [Constants.ACTIVITY_TYPES_DS3.STRATEGIC_ACTIVITY]: "strategic",
    }[activityType];
  }

  public getFormula(formula?: string) {
    if (formula === Constants.FORMULA_STRING.NOFORMULA) return "skpi.no_formula";
    return formula;
  }

  public formatAdaaBoolean(data?: AdaaBooleanType) {
    if (data === AdaaBoolean.N) return "common.form.label.no";
    if (data === AdaaBoolean.Y) return "common.form.label.yes";
    return "common.form.label.n_a";
  }

  public formatFromConstants(key: "measurement" | "freq" | "ytp" | "trend" | "month", id?: number) {
    switch (key) {
      case "freq": {
        const freq = this.frequencies().find((e) => e.id === id);
        return freq ? AdaaHelper.getItemValueByToken(freq, "name") : "";
      }

      case "measurement": {
        const measurement = this.measurementUnits().find((e) => e.id === id);
        return measurement ? AdaaHelper.getItemValueByToken(measurement, "name") : "";
      }

      case "ytp": {
        const ytp = this.ytpCalcs().find((e) => e.id === id);
        return ytp ? AdaaHelper.getItemValueByToken(ytp, "name") : "";
      }

      case "trend": {
        const trend = this.trends().find((e) => e.id === id);
        return trend ? AdaaHelper.getItemValueByToken(trend, "name") : "";
      }

      case "month": {
        const month = this.months().find((e) => e.id === id);
        return month ? AdaaHelper.getItemValueByToken(month, "name") : "";
      }
    }
  }

  private _fetchConstants() {
    const constantIds = [
      Constants.CONSTANT_MEASUREMENT_UNIT,
      Constants.CONSTANT_FREQUENCY,
      Constants.CONSTANT_TREND,
      Constants.CONSTANT_YTP,
      Constants.CONSTANT_MONTHS,
    ];

    this._propertiesService
      .getPropByIdList(constantIds)
      .pipe(filter((res) => !res.inError))
      .subscribe({
        next: (res) => {
          this.ytpCalcs.set(res.responseData.filter(({ propType }) => propType === Constants.CONSTANT_YTP));
          this.trends.set(res.responseData.filter(({ propType }) => propType === Constants.CONSTANT_TREND));
          this.months.set(res.responseData.filter(({ propType }) => propType === Constants.CONSTANT_MONTHS));
          this.frequencies.set(res.responseData.filter(({ propType }) => propType === Constants.CONSTANT_FREQUENCY));
          this.measurementUnits.set(
            res.responseData.filter(({ propType }) => propType === Constants.CONSTANT_MEASUREMENT_UNIT)
          );
        },
      });
  }

  public getInfo(metricType: string) {
    let title = "metrics.global";
    if (metricType === Constants.METRICS_TYPE.LOCAL) title = "metrics.local";
    else if (metricType === Constants.METRICS_TYPE.DIMENSION) title = "main_types.D";

    this.title.set(this._translateService.instant(title));
  }
}
